import { useEffect, useReducer, useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Textarea } from "~/components/ui/textarea";
import {
  AdminApiError,
  createDisasterEvent,
  executeRefunds,
  executeStopOperation,
  fetchPendingRefunds,
  markReturned,
  registerReplacementShipment,
  releaseDisasterHold,
} from "~/lib/admin-api";
import {
  canExecuteStopOperation,
  groupRefundResults,
  initialManualRefundForm,
  initialStopOperationState,
  needsManualReconcile,
  refundResultToastKind,
  REFUND_SELECTION_LIMIT,
  regionTypeLabels,
  selectAllRefundKeys,
  stopOperationReducer,
  targetKey,
  targetTypeLabels,
  toggleTargetSelection,
  validateManualRefundForm,
  type ManualRefundForm,
  type StopOperationInput,
} from "~/lib/disaster-ops";
import { shortId } from "~/lib/format";
import type {
  DisasterTarget,
  DisasterTargetType,
  RefundExecuteResult,
  StopOperationResult,
  StopRegionType,
} from "~/types/admin-disaster";

const permissionErrorMessage =
  "管理者権限がありません。権限が変更された可能性があります — 再ログインしてください。";

function shouldRetry(failureCount: number, error: Error) {
  if (
    error instanceof AdminApiError &&
    error.status >= 400 &&
    error.status < 500
  ) {
    return false;
  }
  return failureCount < 2;
}

function isPermissionError(error: Error | null): boolean {
  return (
    error instanceof AdminApiError &&
    (error.status === 401 ||
      error.status === 403 ||
      error.code === "FORBIDDEN" ||
      error.code === "UNAUTHORIZED")
  );
}

function mutationErrorMessage(error: Error | null): string | null {
  if (!error) return null;
  if (isPermissionError(error)) return permissionErrorMessage;
  if (error instanceof AdminApiError) {
    switch (error.code) {
      case "disaster_event_state_conflict":
        return "災害事象の状態が変わっています。画面を更新してください。";
      case "subscription_state_conflict":
        return "契約の状態が変わっています。画面を更新してください。";
      case "return_state_conflict":
      case "cycle_state_conflict":
        return "対象の状態が変わっています。画面を更新してください。";
      case "shipment_state_conflict":
        return "発送の状態が変わっています。画面を更新してください。";
      case "stop_operation_state_conflict":
        return "停止操作の状態が変わっています。画面を更新してください。";
      case "out_of_stock":
        return "在庫が不足しています。在庫状況を確認してください。";
      case "allocation_overflow":
        return "引当可能数を超えています。対象を確認してください。";
      case "NOT_FOUND":
        return "対象が見つかりません。入力内容を確認してください。";
      case "VALIDATION_ERROR":
        return "入力内容を確認してください。";
    }
    return error.message;
  }
  return "処理に失敗しました。しばらくしてからもう一度お試しください。";
}

function EmptyRow({ columns, label }: { columns: number; label: string }) {
  return (
    <TableRow>
      <TableCell
        colSpan={columns}
        className="h-20 text-center text-muted-foreground"
      >
        {label}
      </TableCell>
    </TableRow>
  );
}

function PreviewCount({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="rounded-lg border bg-muted/30 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function TargetCell({ target }: { target: DisasterTarget }) {
  return (
    <>
      <div>{targetTypeLabels[target.type]}</div>
      <div
        className="font-mono text-xs text-muted-foreground"
        title={target.id}
      >
        {shortId(target.id)}
      </div>
    </>
  );
}

function StopResultSummary({ result }: { result: StopOperationResult }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <PreviewCount label="対象合計" value={result.affected_count} />
      <PreviewCount label="災害停止（契約）" value={result.held_count} />
      <PreviewCount label="スキップ（回）" value={result.skipped_count} />
      <PreviewCount label="返金対象" value={result.refund_count} />
      <PreviewCount label="要確認" value={result.needs_review_count} />
    </div>
  );
}

function TargetListTable({
  caption,
  targets,
  emptyLabel,
}: {
  caption: string;
  targets: readonly DisasterTarget[];
  emptyLabel: string;
}) {
  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="pl-4">{caption}</TableHead>
            <TableHead className="pr-4">ID</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {targets.length === 0 ? (
            <EmptyRow columns={2} label={emptyLabel} />
          ) : (
            targets.map((target) => (
              <TableRow key={targetKey(target)}>
                <TableCell className="pl-4">
                  {targetTypeLabels[target.type]}
                </TableCell>
                <TableCell className="pr-4 font-mono text-xs">
                  {target.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function RefundResultView({ result }: { result: RefundExecuteResult }) {
  const groups = groupRefundResults(result.results);
  const reconcileTargets = needsManualReconcile(result.results);
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <PreviewCount label="依頼" value={result.requested_count} />
        <PreviewCount label="返金済み" value={result.refunded_count} />
        <PreviewCount label="スキップ" value={result.skipped_count} />
        <PreviewCount label="要確認" value={result.needs_review_count} />
        <PreviewCount label="失敗" value={result.failed_count} />
        <PreviewCount label="中断" value={result.aborted_count} />
      </div>
      {result.failed_count > 0 || result.aborted_count > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>
            失敗 {result.failed_count} 件・中断 {result.aborted_count} 件
          </AlertTitle>
          <AlertDescription>
            runbook「手動返金オペレーション」（docs/runbook/20260817-manual-refund-ops.md）を参照してください。中断（aborted）は返金未実行のまま止まった対象で、同じ選択で再実行すれば安全に回収できます。失敗（failed）は理由を確認のうえ runbook の手順で対応してください。
          </AlertDescription>
        </Alert>
      ) : null}
      {reconcileTargets.length > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>手動突合が必要（{reconcileTargets.length} 件）</AlertTitle>
          <AlertDescription>
            runbook「手動返金オペレーション」の突合手順に従ってください。
          </AlertDescription>
        </Alert>
      ) : null}
      {groups.map((group) => (
        <div key={group.kind} className="grid gap-2">
          <div className="flex items-center gap-2">
            <Badge
              variant={
                group.kind === "refunded"
                  ? "secondary"
                  : group.kind === "failed" || group.kind === "aborted"
                    ? "destructive"
                    : "warning"
              }
            >
              {group.label}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {group.results.length} 件
            </span>
          </div>
          <div className="rounded-lg border">
            <Table>
              <TableBody>
                {group.results.map((row) => (
                  <TableRow key={targetKey(row)}>
                    <TableCell className="pl-4">
                      {targetTypeLabels[row.type]}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {row.id}
                    </TableCell>
                    <TableCell className="pr-4 font-mono text-xs text-muted-foreground">
                      {row.result === "refunded"
                        ? row.refund_payjp_id
                        : row.reason}
                      {row.result === "refunded" && row.audit_log_failed
                        ? "（監査ログ記録失敗）"
                        : ""}
                      {row.result === "skipped" && row.needs_manual_reconcile
                        ? "（手動突合が必要）"
                        : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Disaster({ accessToken }: { accessToken: string }) {
  // ① 災害事象の作成
  const [eventName, setEventName] = useState("");
  const [eventEvidence, setEventEvidence] = useState("");

  // ② 停止操作
  const [eventId, setEventId] = useState("");
  const [regionType, setRegionType] = useState<StopRegionType>("prefecture");
  const [regionValue, setRegionValue] = useState("");
  const [stopState, dispatchStop] = useReducer(
    stopOperationReducer,
    initialStopOperationState,
  );

  // ③ 返金キュー
  const [selectedRefundKeys, setSelectedRefundKeys] = useState<string[]>([]);
  const [refundConfirmOpen, setRefundConfirmOpen] = useState(false);
  const [refundResult, setRefundResult] = useState<RefundExecuteResult | null>(
    null,
  );

  // ④ 個別解除
  const [releaseSubscriptionId, setReleaseSubscriptionId] = useState("");
  const [releaseReason, setReleaseReason] = useState("");

  // ⑤ 未受取終了
  const [returnKind, setReturnKind] = useState<DisasterTargetType>("cycle");
  const [returnTargetId, setReturnTargetId] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [returnConfirmOpen, setReturnConfirmOpen] = useState(false);

  // ⑥ 交換出荷
  const [replacementKind, setReplacementKind] =
    useState<DisasterTargetType>("cycle");
  const [replacementTargetId, setReplacementTargetId] = useState("");
  const [replacementSupersedes, setReplacementSupersedes] = useState("");
  const [replacementTrackingNumber, setReplacementTrackingNumber] =
    useState("");
  const [replacementConfirmOpen, setReplacementConfirmOpen] = useState(false);

  // ⑦ 任意対象の返金（A7）
  const [manualRefundForm, setManualRefundForm] = useState<ManualRefundForm>(
    initialManualRefundForm,
  );
  const [manualRefundConfirmOpen, setManualRefundConfirmOpen] = useState(false);
  const [manualRefundResult, setManualRefundResult] =
    useState<RefundExecuteResult | null>(null);

  const pendingRefundsQuery = useQuery({
    queryKey: ["pending-refunds"],
    queryFn: ({ signal }) => fetchPendingRefunds(accessToken, signal),
    refetchInterval: 60_000,
    retry: shouldRetry,
  });

  const createEventMutation = useMutation({
    mutationFn: () =>
      createDisasterEvent(accessToken, {
        name: eventName.trim(),
        evidence: eventEvidence.trim(),
      }),
    onSuccess: (event) => {
      setEventId(event.id);
      dispatchStop({ type: "input_changed" });
      toast.success("災害事象を作成しました。");
    },
  });

  const previewStopMutation = useMutation({
    mutationFn: (input: StopOperationInput) =>
      executeStopOperation(accessToken, input.event_id, {
        region_type: input.region_type,
        region_value: input.region_value,
        dry_run: true,
      }),
    onMutate: () => dispatchStop({ type: "preview_started" }),
    onSuccess: (preview, input) =>
      dispatchStop({ type: "preview_received", input, preview }),
    onError: () => dispatchStop({ type: "preview_failed" }),
  });

  // 確定はプレビュー時に固定した入力（stopState.input）から送る — 入力欄の現在値は使わない
  const executeStopMutation = useMutation({
    mutationFn: (input: StopOperationInput) =>
      executeStopOperation(accessToken, input.event_id, {
        region_type: input.region_type,
        region_value: input.region_value,
        dry_run: false,
      }),
    onMutate: () => dispatchStop({ type: "execute_started" }),
    onSuccess: async (result) => {
      dispatchStop({ type: "execute_received", result });
      await pendingRefundsQuery.refetch();
      toast.success(`災害停止を確定しました（対象 ${result.affected_count} 件）。`);
    },
    onError: () => dispatchStop({ type: "execute_failed" }),
  });

  const refundMutation = useMutation({
    mutationFn: (targets: DisasterTarget[]) =>
      executeRefunds(accessToken, { targets }),
    onSuccess: async (result) => {
      setRefundResult(result);
      setRefundConfirmOpen(false);
      setSelectedRefundKeys([]);
      await pendingRefundsQuery.refetch();
      toast.success(`返金実行が完了しました（返金済み ${result.refunded_count} 件）。`);
    },
  });

  const manualRefundMutation = useMutation({
    mutationFn: () => {
      const validated = validateManualRefundForm(manualRefundForm);
      if (!validated.ok) {
        return Promise.reject(new Error("入力内容を確認してください。"));
      }
      return executeRefunds(accessToken, validated.request);
    },
    onSuccess: async (result) => {
      setManualRefundResult(result);
      setManualRefundConfirmOpen(false);
      setManualRefundForm(initialManualRefundForm);
      await pendingRefundsQuery.refetch();
      if (refundResultToastKind(result) === "success") {
        toast.success(
          `返金実行が完了しました（返金済み ${result.refunded_count} 件）。`,
        );
      } else {
        toast.warning(
          "返金は実行されませんでした。結果の内訳を確認してください。",
        );
      }
    },
  });

  const releaseMutation = useMutation({
    mutationFn: () =>
      releaseDisasterHold(
        accessToken,
        releaseSubscriptionId.trim(),
        releaseReason.trim(),
      ),
    onSuccess: async (result) => {
      setReleaseSubscriptionId("");
      setReleaseReason("");
      await pendingRefundsQuery.refetch();
      toast.success(
        result.result === "released"
          ? "災害停止を解除しました。"
          : "すでに解除済みです（変更なし）。",
      );
    },
  });

  const returnMutation = useMutation({
    mutationFn: () =>
      markReturned(accessToken, {
        target: { type: returnKind, id: returnTargetId.trim() },
        reason: returnReason.trim(),
      }),
    onSuccess: () => {
      setReturnConfirmOpen(false);
      setReturnTargetId("");
      setReturnReason("");
      toast.success("未受取終了を記録しました。");
    },
  });

  const replacementMutation = useMutation({
    mutationFn: () =>
      registerReplacementShipment(accessToken, {
        target_kind: replacementKind,
        target_id: replacementTargetId.trim(),
        tracking_number: replacementTrackingNumber.trim(),
        supersedes_shipment_id: replacementSupersedes.trim(),
      }),
    onSuccess: (result) => {
      setReplacementConfirmOpen(false);
      if (result.result === "registered") {
        setReplacementTargetId("");
        setReplacementSupersedes("");
        setReplacementTrackingNumber("");
      }
      toast.success(
        result.result === "registered"
          ? "交換出荷を登録しました。"
          : "同じ内容が登録済みのためスキップしました。",
      );
    },
  });

  useEffect(() => {
    const available = new Set(
      pendingRefundsQuery.data?.refund_targets.map(targetKey) ?? [],
    );
    setSelectedRefundKeys((current) =>
      current.filter((key) => available.has(key)),
    );
  }, [pendingRefundsQuery.data]);

  function handleCreateEvent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !eventName.trim() ||
      !eventEvidence.trim() ||
      createEventMutation.isPending
    ) {
      return;
    }
    createEventMutation.mutate();
  }

  function handlePreviewStop(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !eventId.trim() ||
      !regionValue.trim() ||
      previewStopMutation.isPending ||
      executeStopMutation.isPending
    ) {
      return;
    }
    executeStopMutation.reset();
    previewStopMutation.mutate({
      event_id: eventId.trim(),
      region_type: regionType,
      region_value: regionValue.trim(),
    });
  }

  function handleExecuteStop() {
    if (
      stopState.phase !== "ready" ||
      !canExecuteStopOperation(stopState) ||
      executeStopMutation.isPending
    ) {
      return;
    }
    executeStopMutation.mutate(stopState.input);
  }

  // 入力が変わったらプレビューを無効化する（陳腐化したプレビューでの確定を防ぐ）
  function handleStopInputChange() {
    dispatchStop({ type: "input_changed" });
  }

  const refundTargets = pendingRefundsQuery.data?.refund_targets ?? [];
  const selectedRefundTargets = refundTargets.filter((target) =>
    selectedRefundKeys.includes(targetKey(target)),
  );
  const allRefundsSelected =
    refundTargets.length > 0 &&
    selectedRefundKeys.length ===
      Math.min(refundTargets.length, REFUND_SELECTION_LIMIT);
  const refundSelectionOverLimit =
    selectedRefundTargets.length > REFUND_SELECTION_LIMIT;

  const createEventError = mutationErrorMessage(createEventMutation.error);
  const previewStopError = mutationErrorMessage(previewStopMutation.error);
  const executeStopError = mutationErrorMessage(executeStopMutation.error);
  const refundError = mutationErrorMessage(refundMutation.error);
  const manualRefundError = mutationErrorMessage(manualRefundMutation.error);
  const releaseError = mutationErrorMessage(releaseMutation.error);
  const returnError = mutationErrorMessage(returnMutation.error);
  const replacementError = mutationErrorMessage(replacementMutation.error);

  if (
    pendingRefundsQuery.error &&
    isPermissionError(pendingRefundsQuery.error)
  ) {
    return (
      <Alert variant="destructive" className="mx-auto max-w-2xl">
        <AlertTitle>権限がありません</AlertTitle>
        <AlertDescription>{permissionErrorMessage}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">災害停止</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            災害事象の作成、災害停止の実行、返金キュー、個別解除、未受取終了、交換出荷、任意対象の返金を行います。
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void pendingRefundsQuery.refetch()}
          disabled={pendingRefundsQuery.isFetching}
        >
          {pendingRefundsQuery.isFetching ? "更新中…" : "返金キューを更新"}
        </Button>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>1. 災害事象を作成する</CardTitle>
            <CardDescription>
              事象名と根拠（公的情報の URL など）を記録します。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid gap-4" onSubmit={handleCreateEvent}>
              <div className="grid gap-2">
                <Label htmlFor="disaster-event-name">事象名</Label>
                <Input
                  id="disaster-event-name"
                  required
                  value={eventName}
                  onChange={(event) => setEventName(event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="disaster-event-evidence">根拠</Label>
                <Textarea
                  id="disaster-event-evidence"
                  rows={2}
                  required
                  value={eventEvidence}
                  onChange={(event) => setEventEvidence(event.target.value)}
                />
              </div>
              {createEventError ? (
                <Alert variant="destructive">
                  <AlertDescription>{createEventError}</AlertDescription>
                </Alert>
              ) : null}
              <div className="flex justify-end">
                <Button type="submit" disabled={createEventMutation.isPending}>
                  {createEventMutation.isPending ? "作成中…" : "災害事象を作成"}
                </Button>
              </div>
            </form>
            {createEventMutation.data ? (
              <Alert className="mt-4">
                <AlertTitle>作成しました</AlertTitle>
                <AlertDescription>
                  <span className="font-mono text-xs">
                    {createEventMutation.data.id}
                  </span>
                  （停止操作の事象 ID に自動入力済み）
                </AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>4. 個別解除</CardTitle>
            <CardDescription>
              契約 1 件の災害停止を解除します（理由は必須）。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (
                  !releaseSubscriptionId.trim() ||
                  !releaseReason.trim() ||
                  releaseMutation.isPending
                ) {
                  return;
                }
                releaseMutation.mutate();
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="release-subscription-id">契約 ID</Label>
                <Input
                  id="release-subscription-id"
                  required
                  value={releaseSubscriptionId}
                  onChange={(event) =>
                    setReleaseSubscriptionId(event.target.value)
                  }
                  placeholder="UUID"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="release-reason">理由</Label>
                <Textarea
                  id="release-reason"
                  rows={2}
                  required
                  value={releaseReason}
                  onChange={(event) => setReleaseReason(event.target.value)}
                />
              </div>
              {releaseError ? (
                <Alert variant="destructive">
                  <AlertDescription>{releaseError}</AlertDescription>
                </Alert>
              ) : null}
              <div className="flex justify-end">
                <Button type="submit" disabled={releaseMutation.isPending}>
                  {releaseMutation.isPending ? "解除中…" : "災害停止を解除"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>2. 災害停止を実行する</CardTitle>
          <CardDescription>
            地域を指定して対象を確認（dry-run）し、内容を再確認のうえ確定します。
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <form
            className="grid items-end gap-4 lg:grid-cols-[1fr_14rem_1fr_auto]"
            onSubmit={handlePreviewStop}
          >
            <div className="grid gap-2">
              <Label htmlFor="stop-event-id">事象 ID</Label>
              <Input
                id="stop-event-id"
                required
                value={eventId}
                onChange={(event) => {
                  setEventId(event.target.value);
                  handleStopInputChange();
                }}
                placeholder="UUID"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="stop-region-type">地域指定</Label>
              <select
                id="stop-region-type"
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                value={regionType}
                onChange={(event) => {
                  setRegionType(event.target.value as StopRegionType);
                  handleStopInputChange();
                }}
              >
                <option value="prefecture">{regionTypeLabels.prefecture}</option>
                <option value="postal_prefix">
                  {regionTypeLabels.postal_prefix}
                </option>
              </select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="stop-region-value">地域の値</Label>
              <Input
                id="stop-region-value"
                required
                value={regionValue}
                onChange={(event) => {
                  setRegionValue(event.target.value);
                  handleStopInputChange();
                }}
                placeholder={
                  regionType === "prefecture" ? "例: 宮城県" : "例: 980"
                }
              />
            </div>
            <Button
              type="submit"
              disabled={
                previewStopMutation.isPending || executeStopMutation.isPending
              }
            >
              {previewStopMutation.isPending ? "確認中…" : "対象を確認"}
            </Button>
          </form>

          {previewStopError ? (
            <Alert variant="destructive">
              <AlertTitle>対象を確認できませんでした</AlertTitle>
              <AlertDescription>{previewStopError}</AlertDescription>
            </Alert>
          ) : null}

          {stopState.phase === "ready" || stopState.phase === "executing" ? (
            <div className="grid gap-4 rounded-lg border p-4">
              <StopResultSummary result={stopState.preview} />
              <div className="grid gap-4 xl:grid-cols-2">
                <TargetListTable
                  caption="返金対象"
                  targets={stopState.preview.refund_targets}
                  emptyLabel="返金対象はありません。"
                />
                <TargetListTable
                  caption="要確認（引受未確認の発送あり）"
                  targets={stopState.preview.needs_review}
                  emptyLabel="要確認の対象はありません。"
                />
              </div>
              <Alert>
                <AlertDescription>
                  確定すると対象契約が災害停止になり、予定されていた回はスキップされます。返金はこの操作では実行されません（返金キューから実行します）。
                </AlertDescription>
              </Alert>
              {executeStopError ? (
                <Alert variant="destructive">
                  <AlertDescription>{executeStopError}</AlertDescription>
                </Alert>
              ) : null}
              <div className="flex flex-wrap items-center justify-between gap-4 border-t pt-4">
                <label className="flex items-center gap-3 text-sm font-medium">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={
                      stopState.phase === "ready" ? stopState.confirmed : true
                    }
                    disabled={stopState.phase !== "ready"}
                    onChange={(event) =>
                      dispatchStop({
                        type: "confirm_toggled",
                        confirmed: event.target.checked,
                      })
                    }
                  />
                  件数と対象を確認しました
                </label>
                <Button
                  onClick={handleExecuteStop}
                  disabled={
                    !canExecuteStopOperation(stopState) ||
                    executeStopMutation.isPending
                  }
                >
                  {executeStopMutation.isPending
                    ? "確定中…"
                    : `災害停止を確定（対象 ${stopState.preview.affected_count} 件）`}
                </Button>
              </div>
            </div>
          ) : null}

          {stopState.phase === "complete" ? (
            <div className="grid gap-4">
              <Alert>
                <AlertTitle>災害停止を確定しました</AlertTitle>
                <AlertDescription>
                  災害停止 {stopState.result.held_count} 件、スキップ{" "}
                  {stopState.result.skipped_count} 件。返金対象{" "}
                  {stopState.result.refund_count} 件は返金キューから実行してください。
                </AlertDescription>
              </Alert>
              <StopResultSummary result={stopState.result} />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>3. 返金キュー</CardTitle>
          <CardDescription>
            災害停止で返金対象になった課金済みの回・単品便です。要確認は引受未確認のため自動返金の対象外です。
          </CardDescription>
          <CardAction>
            <Badge
              variant={
                pendingRefundsQuery.data?.refund_count ? "warning" : "secondary"
              }
            >
              返金対象 {pendingRefundsQuery.data?.refund_count ?? "—"}
            </Badge>
          </CardAction>
        </CardHeader>
        <CardContent className="grid gap-6 px-0">
          {pendingRefundsQuery.error ? (
            <Alert variant="destructive" className="mx-6">
              <AlertTitle>返金キューを取得できませんでした</AlertTitle>
              <AlertDescription>
                {mutationErrorMessage(pendingRefundsQuery.error)}
              </AlertDescription>
            </Alert>
          ) : null}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 pl-6">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    aria-label="返金対象をすべて選択"
                    checked={allRefundsSelected}
                    onChange={(event) =>
                      setSelectedRefundKeys(
                        event.target.checked
                          ? selectAllRefundKeys(refundTargets)
                          : [],
                      )
                    }
                    disabled={refundTargets.length === 0}
                  />
                </TableHead>
                <TableHead>対象</TableHead>
                <TableHead className="pr-6">ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingRefundsQuery.isPending ? (
                <EmptyRow columns={3} label="返金キューを読み込んでいます…" />
              ) : refundTargets.length > 0 ? (
                refundTargets.map((target) => {
                  const key = targetKey(target);
                  return (
                    <TableRow key={key}>
                      <TableCell className="pl-6">
                        <input
                          type="checkbox"
                          className="size-4 accent-primary"
                          aria-label={`${targetTypeLabels[target.type]} ${target.id} を選択`}
                          checked={selectedRefundKeys.includes(key)}
                          onChange={(event) =>
                            setSelectedRefundKeys((current) =>
                              toggleTargetSelection(
                                current,
                                key,
                                event.target.checked,
                              ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>{targetTypeLabels[target.type]}</TableCell>
                      <TableCell className="pr-6 font-mono text-xs">
                        {target.id}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <EmptyRow columns={3} label="返金対象はありません。" />
              )}
            </TableBody>
          </Table>

          <div className="grid gap-3 border-t px-6 pt-4">
            {refundTargets.length > REFUND_SELECTION_LIMIT ? (
              <p className="text-sm text-muted-foreground">
                全選択は先頭 {REFUND_SELECTION_LIMIT} 件までです（残り{" "}
                {refundTargets.length - REFUND_SELECTION_LIMIT}{" "}
                件は次の実行で選択してください）。
              </p>
            ) : null}
            {refundSelectionOverLimit ? (
              <Alert variant="destructive">
                <AlertDescription>
                  1 回の返金実行は {REFUND_SELECTION_LIMIT} 件までです。選択を{" "}
                  {REFUND_SELECTION_LIMIT} 件以下に減らし、分割して実行してください。
                </AlertDescription>
              </Alert>
            ) : null}
            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button
                onClick={() => {
                  if (
                    selectedRefundTargets.length === 0 ||
                    refundSelectionOverLimit
                  ) {
                    return;
                  }
                  refundMutation.reset();
                  setRefundConfirmOpen(true);
                }}
                disabled={
                  selectedRefundTargets.length === 0 ||
                  refundSelectionOverLimit ||
                  refundMutation.isPending
                }
              >
                {`選択した ${selectedRefundTargets.length} 件の返金内容を確認`}
              </Button>
            </div>
          </div>

          <div className="grid gap-3 px-6">
            <div className="flex items-center gap-2">
              <Badge
                variant={
                  pendingRefundsQuery.data?.needs_review_count
                    ? "warning"
                    : "secondary"
                }
              >
                要確認 {pendingRefundsQuery.data?.needs_review_count ?? "—"}
              </Badge>
              <span className="text-sm text-muted-foreground">
                追跡番号登録済み・引受未確認の発送がある対象です。引受を記録するか、状況を確認してから個別に判断します。
              </span>
            </div>
            <TargetListTable
              caption="要確認"
              targets={pendingRefundsQuery.data?.needs_review ?? []}
              emptyLabel="要確認の対象はありません。"
            />
          </div>

          {refundResult ? (
            <div className="grid gap-4 px-6">
              <h3 className="font-medium">返金実行の結果</h3>
              <RefundResultView result={refundResult} />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>5. 未受取終了</CardTitle>
            <CardDescription>
              保管期間満了などで受け取られなかった回・単品便を終了します（返金・再送なし・理由は必須）。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (!returnTargetId.trim() || !returnReason.trim()) return;
                returnMutation.reset();
                setReturnConfirmOpen(true);
              }}
            >
              <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
                <div className="grid gap-2">
                  <Label htmlFor="return-target-kind">対象種別</Label>
                  <select
                    id="return-target-kind"
                    className="h-9 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                    value={returnKind}
                    onChange={(event) =>
                      setReturnKind(event.target.value as DisasterTargetType)
                    }
                  >
                    <option value="cycle">{targetTypeLabels.cycle}</option>
                    <option value="one_time_order">
                      {targetTypeLabels.one_time_order}
                    </option>
                  </select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="return-target-id">対象 ID</Label>
                  <Input
                    id="return-target-id"
                    required
                    value={returnTargetId}
                    onChange={(event) => setReturnTargetId(event.target.value)}
                    placeholder="UUID"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="return-reason">理由</Label>
                <Textarea
                  id="return-reason"
                  rows={2}
                  required
                  value={returnReason}
                  onChange={(event) => setReturnReason(event.target.value)}
                />
              </div>
              {returnError ? (
                <Alert variant="destructive">
                  <AlertDescription>{returnError}</AlertDescription>
                </Alert>
              ) : null}
              <div className="flex justify-end">
                <Button type="submit">終了内容を確認</Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>6. 交換出荷</CardTitle>
            <CardDescription>
              元の発送（shipment）を指定し、新しい追跡番号で交換分を登録します。課金は発生しません。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (
                  !replacementTargetId.trim() ||
                  !replacementSupersedes.trim() ||
                  !replacementTrackingNumber.trim()
                ) {
                  return;
                }
                replacementMutation.reset();
                setReplacementConfirmOpen(true);
              }}
            >
              <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
                <div className="grid gap-2">
                  <Label htmlFor="replacement-target-kind">対象種別</Label>
                  <select
                    id="replacement-target-kind"
                    className="h-9 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                    value={replacementKind}
                    onChange={(event) =>
                      setReplacementKind(
                        event.target.value as DisasterTargetType,
                      )
                    }
                  >
                    <option value="cycle">{targetTypeLabels.cycle}</option>
                    <option value="one_time_order">
                      {targetTypeLabels.one_time_order}
                    </option>
                  </select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="replacement-target-id">対象 ID</Label>
                  <Input
                    id="replacement-target-id"
                    required
                    value={replacementTargetId}
                    onChange={(event) =>
                      setReplacementTargetId(event.target.value)
                    }
                    placeholder="UUID"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="replacement-supersedes">
                  元の発送（shipment）ID
                </Label>
                <Input
                  id="replacement-supersedes"
                  required
                  value={replacementSupersedes}
                  onChange={(event) =>
                    setReplacementSupersedes(event.target.value)
                  }
                  placeholder="UUID"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="replacement-tracking-number">
                  新しい追跡番号
                </Label>
                <Input
                  id="replacement-tracking-number"
                  required
                  inputMode="numeric"
                  value={replacementTrackingNumber}
                  onChange={(event) =>
                    setReplacementTrackingNumber(event.target.value)
                  }
                  placeholder="12桁（空白・ハイフン可）"
                />
              </div>
              {replacementError ? (
                <Alert variant="destructive">
                  <AlertDescription>{replacementError}</AlertDescription>
                </Alert>
              ) : null}
              <div className="flex justify-end">
                <Button type="submit">登録内容を確認</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>7. 任意対象の返金</CardTitle>
          <CardDescription>
            災害停止に依らず、課金済みの回・単品便を直接指定して全額返金します（返品・オペレーションミス起因の受け皿・理由は必須）。
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <form
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!validateManualRefundForm(manualRefundForm).ok) return;
              manualRefundMutation.reset();
              setManualRefundConfirmOpen(true);
            }}
          >
            <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
              <div className="grid gap-2">
                <Label htmlFor="manual-refund-target-kind">対象種別</Label>
                <select
                  id="manual-refund-target-kind"
                  className="h-9 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                  value={manualRefundForm.targetType}
                  onChange={(event) =>
                    setManualRefundForm((form) => ({
                      ...form,
                      targetType: event.target.value as DisasterTargetType,
                    }))
                  }
                >
                  <option value="cycle">{targetTypeLabels.cycle}</option>
                  <option value="one_time_order">
                    {targetTypeLabels.one_time_order}
                  </option>
                </select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="manual-refund-target-id">対象 ID</Label>
                <Input
                  id="manual-refund-target-id"
                  required
                  value={manualRefundForm.targetId}
                  onChange={(event) =>
                    setManualRefundForm((form) => ({
                      ...form,
                      targetId: event.target.value,
                    }))
                  }
                  placeholder="UUID"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="manual-refund-reason">理由</Label>
              <Textarea
                id="manual-refund-reason"
                rows={2}
                required
                value={manualRefundForm.reason}
                onChange={(event) =>
                  setManualRefundForm((form) => ({
                    ...form,
                    reason: event.target.value,
                  }))
                }
              />
            </div>
            <label className="flex items-center gap-3 text-sm font-medium">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={manualRefundForm.overrideShipmentGuard}
                onChange={(event) =>
                  setManualRefundForm((form) => ({
                    ...form,
                    overrideShipmentGuard: event.target.checked,
                  }))
                }
              />
              発送済みでも返金する（引受記録があっても実行されます）
            </label>
            {manualRefundError ? (
              <Alert variant="destructive">
                <AlertDescription>{manualRefundError}</AlertDescription>
              </Alert>
            ) : null}
            <div className="flex justify-end">
              <Button type="submit">返金内容を確認</Button>
            </div>
          </form>

          {manualRefundResult ? (
            <div className="grid gap-4">
              <h3 className="font-medium">返金実行の結果</h3>
              <RefundResultView result={manualRefundResult} />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog
        open={manualRefundConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !manualRefundMutation.isPending) {
            setManualRefundConfirmOpen(false);
          }
        }}
      >
        <DialogContent
          aria-labelledby="manual-refund-dialog-title"
          aria-describedby="manual-refund-dialog-description"
        >
          <form
            className="grid gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              if (manualRefundMutation.isPending) return;
              manualRefundMutation.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle id="manual-refund-dialog-title">
                この対象を返金しますか？
              </DialogTitle>
              <DialogDescription id="manual-refund-dialog-description">
                pay.jp で全額返金が実行されます。この操作は取り消せません。
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 px-6 text-sm">
              <div className="grid grid-cols-[7rem_1fr] gap-2">
                <span className="text-muted-foreground">対象</span>
                <span>
                  {targetTypeLabels[manualRefundForm.targetType]} /{" "}
                  <span className="font-mono text-xs">
                    {manualRefundForm.targetId.trim()}
                  </span>
                </span>
                <span className="text-muted-foreground">理由</span>
                <span>{manualRefundForm.reason.trim()}</span>
              </div>
              {manualRefundForm.overrideShipmentGuard ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    発送済みでも返金します。引受記録（引受済み・引受未確認）があっても返金が実行されます。
                  </AlertDescription>
                </Alert>
              ) : null}
              {manualRefundError ? (
                <Alert variant="destructive">
                  <AlertDescription>{manualRefundError}</AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setManualRefundConfirmOpen(false)}
                disabled={manualRefundMutation.isPending}
              >
                キャンセル
              </Button>
              <Button type="submit" disabled={manualRefundMutation.isPending}>
                {manualRefundMutation.isPending ? "返金中…" : "返金を確定"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={refundConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !refundMutation.isPending) setRefundConfirmOpen(false);
        }}
      >
        <DialogContent
          aria-labelledby="refund-dialog-title"
          aria-describedby="refund-dialog-description"
        >
          <form
            className="grid gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              if (refundMutation.isPending) return;
              refundMutation.mutate(selectedRefundTargets);
            }}
          >
            <DialogHeader>
              <DialogTitle id="refund-dialog-title">
                {selectedRefundTargets.length} 件を返金しますか？
              </DialogTitle>
              <DialogDescription id="refund-dialog-description">
                pay.jp で全額返金が実行されます。この操作は取り消せません。
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 px-6 text-sm">
              <div className="max-h-64 overflow-y-auto rounded-lg border">
                <Table>
                  <TableBody>
                    {selectedRefundTargets.map((target) => (
                      <TableRow key={targetKey(target)}>
                        <TableCell className="pl-4">
                          {targetTypeLabels[target.type]}
                        </TableCell>
                        <TableCell className="pr-4 font-mono text-xs">
                          {target.id}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {refundError ? (
                <Alert variant="destructive">
                  <AlertDescription>{refundError}</AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRefundConfirmOpen(false)}
                disabled={refundMutation.isPending}
              >
                キャンセル
              </Button>
              <Button type="submit" disabled={refundMutation.isPending}>
                {refundMutation.isPending
                  ? "返金中…"
                  : `${selectedRefundTargets.length} 件の返金を確定`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={returnConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !returnMutation.isPending) setReturnConfirmOpen(false);
        }}
      >
        <DialogContent
          aria-labelledby="return-dialog-title"
          aria-describedby="return-dialog-description"
        >
          <form
            className="grid gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              if (returnMutation.isPending) return;
              returnMutation.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle id="return-dialog-title">
                未受取終了にしますか？
              </DialogTitle>
              <DialogDescription id="return-dialog-description">
                対象は返金・再送なしで終了します。
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 px-6 text-sm">
              <div className="grid grid-cols-[7rem_1fr] gap-2">
                <span className="text-muted-foreground">対象</span>
                <span>
                  {targetTypeLabels[returnKind]} / {returnTargetId.trim()}
                </span>
                <span className="text-muted-foreground">理由</span>
                <span>{returnReason.trim()}</span>
              </div>
              {returnError ? (
                <Alert variant="destructive">
                  <AlertDescription>{returnError}</AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setReturnConfirmOpen(false)}
                disabled={returnMutation.isPending}
              >
                キャンセル
              </Button>
              <Button type="submit" disabled={returnMutation.isPending}>
                {returnMutation.isPending ? "記録中…" : "未受取終了を確定"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={replacementConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !replacementMutation.isPending) {
            setReplacementConfirmOpen(false);
          }
        }}
      >
        <DialogContent
          aria-labelledby="replacement-dialog-title"
          aria-describedby="replacement-dialog-description"
        >
          <form
            className="grid gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              if (replacementMutation.isPending) return;
              replacementMutation.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle id="replacement-dialog-title">
                交換出荷を登録しますか？
              </DialogTitle>
              <DialogDescription id="replacement-dialog-description">
                確定すると発送通知メールが送信されます。
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 px-6 text-sm">
              <div className="grid grid-cols-[9rem_1fr] gap-2">
                <span className="text-muted-foreground">対象</span>
                <span>
                  {targetTypeLabels[replacementKind]} /{" "}
                  {replacementTargetId.trim()}
                </span>
                <span className="text-muted-foreground">元の発送</span>
                <span className="font-mono">
                  {replacementSupersedes.trim()}
                </span>
                <span className="text-muted-foreground">新しい追跡番号</span>
                <span className="font-mono">
                  {replacementTrackingNumber.trim()}
                </span>
              </div>
              {replacementError ? (
                <Alert variant="destructive">
                  <AlertDescription>{replacementError}</AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setReplacementConfirmOpen(false)}
                disabled={replacementMutation.isPending}
              >
                キャンセル
              </Button>
              <Button type="submit" disabled={replacementMutation.isPending}>
                {replacementMutation.isPending ? "登録中…" : "交換出荷を確定"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
